import React, { memo, useCallback, useEffect, useState } from "react";
import { useIsMobile } from "hooks/useIsMobile";
import DatePicker from "react-datepicker";
import {
  ReferralCreditConfigAPI,
  ReferralCreditConfigRequest,
} from "apis/adminServices/referralServices";
import { useToastStore } from "store/toastStore";

const toLocalDateString = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

interface EditReferralModalProps {
  isOpen: boolean;
  config: ReferralCreditConfigAPI;
  onClose: () => void;
  onUpdate: (
    id: number,
    payload: ReferralCreditConfigRequest,
  ) => Promise<void> | void;
}

const toFormState = (
  c: ReferralCreditConfigAPI,
): ReferralCreditConfigRequest => ({
  configName: c.configName,
  referrerCreditAmount: c.referrerCreditAmount,
  refereeCreditAmount: c.refereeCreditAmount,
  nonCommunityReferrerCredit: c.nonCommunityReferrerCredit ?? 0,
  nonCommunityRefereeCredit: c.nonCommunityRefereeCredit ?? 0,
  communityCount: c.communityCount ?? 0,
  isActive: c.isActive,
  validTill: c.validTill,
});

const EditReferralModal: React.FC<EditReferralModalProps> = memo(
  ({ isOpen, config, onClose, onUpdate }) => {
    const [formData, setFormData] = useState<ReferralCreditConfigRequest>(
      toFormState(config),
    );
    const [loading, setLoading] = useState(false);
    const { showToast } = useToastStore();
    const isMobile = useIsMobile();

    useEffect(() => {
      if (isOpen) {
        setFormData(toFormState(config));
      }
    }, [isOpen, config]);

    const handleFieldChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { id, value, type } = e.target;
        setFormData((prev) => ({
          ...prev,
          [id]:
            type === "number"
              ? parseInt(value, 10) || 0
              : id === "isActive"
                ? value === "true"
                : value,
        }));
      },
      [],
    );

    const handleValidTillChange = useCallback((date: Date | null) => {
      if (!date) {
        setFormData((prev) => ({ ...prev, validTill: "" }));
        return;
      }
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const selected = new Date(date);
      selected.setHours(0, 0, 0, 0);
      if (selected.getTime() < today.getTime()) {
        showToast("Cannot select past dates.", "error");
        return;
      }
      setFormData((prev) => ({ ...prev, validTill: toLocalDateString(date) }));
    }, [showToast]);

    const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault();
      // "Valid Till" is a DatePicker, so native `required` doesn't apply — check it.
      if (!formData.validTill) {
        showToast("Please select a Valid Till date.", "error");
        return;
      }
      setLoading(true);
      try {
        await onUpdate(config.id, formData);
      } finally {
        setLoading(false);
      }
    };

    if (!isOpen) return null;

    return (
      <div
        className="fixed inset-0 z-50 flex overflow-y-auto bg-black bg-opacity-50 p-4"
        onClick={onClose}
      >
        <div
          className="relative w-full max-w-xl m-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="relative bg-white rounded-lg shadow pb-4">
            <div className="flex justify-between mx-4 py-4 rounded-t border-b border-lightgray-100">
              <p className="text-base font-semibold text-grease-100">
                Edit ({formData.configName})
              </p>
              <button
                type="button"
                className="text-gray-400 bg-transparent rounded-lg text-sm w-8 h-8 inline-flex justify-center items-center"
                onClick={onClose}
                disabled={loading}
              >
                <svg className="w-3 h-3" viewBox="0 0 14 14" fill="none">
                  <path
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
                  />
                </svg>
                <span className="sr-only">Close</span>
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="px-4 py-4">
                {/* Config Name */}
                <div className="mb-4">
                  <label
                    className="block text-xs text-gray-500 mb-2"
                    htmlFor="configName"
                  >
                    Configuration Name
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                  <input
                    type="text"
                    id="configName"
                    value={formData.configName}
                    onChange={handleFieldChange}
                    className="w-full p-3 rounded-lg bg-lightblue-100 text-xs text-grease-100 focus:outline-none border-none"
                    required
                    disabled={loading}
                  />
                </div>

                {/* Status */}
                <div className="mb-4">
                  <label
                    className="block text-xs text-gray-500 mb-2"
                    htmlFor="isActive"
                  >
                    Status
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                  <select
                    id="isActive"
                    value={String(formData.isActive)}
                    onChange={handleFieldChange}
                    className="w-full p-3 rounded-lg bg-lightblue-100 text-xs text-grease-100 focus:outline-none border-none"
                    disabled={loading}
                  >
                    <option value="true">Active</option>
                    <option value="false">Disable</option>
                  </select>
                </div>

                {/* Community Credits */}
                <div className="mb-4 flex gap-4">
                  <div className="flex-1">
                    <label
                      className="block text-xs text-gray-500 mb-2"
                      htmlFor="referrerCreditAmount"
                    >
                      Community Referrer Credit Amount
                      <span className="text-red-500 ml-1">*</span>
                    </label>
                    <input
                      type="number"
                      id="referrerCreditAmount"
                      value={formData.referrerCreditAmount === 0 ? "" : formData.referrerCreditAmount}
                      onChange={handleFieldChange}
                      min="0"
                      placeholder="0"
                      className="w-full p-3 rounded-lg bg-lightblue-100 text-xs text-grease-100 focus:outline-none border-none"
                      disabled={loading}
                    />
                  </div>
                  <div className="flex-1">
                    <label
                      className="block text-xs text-gray-500 mb-2"
                      htmlFor="refereeCreditAmount"
                    >
                      Community Referee Credit Amount
                      <span className="text-red-500 ml-1">*</span>
                    </label>
                    <input
                      type="number"
                      id="refereeCreditAmount"
                      value={formData.refereeCreditAmount === 0 ? "" : formData.refereeCreditAmount}
                      onChange={handleFieldChange}
                      min="0"
                      placeholder="0"
                      className="w-full p-3 rounded-lg bg-lightblue-100 text-xs text-grease-100 focus:outline-none border-none"
                      disabled={loading}
                    />
                  </div>
                </div>

                {/* Non-Community Credits */}
                <div className="mb-4 flex gap-4">
                  <div className="flex-1">
                    <label
                      className="block text-xs text-gray-500 mb-2"
                      htmlFor="nonCommunityReferrerCredit"
                    >
                      Non-Community Referrer Credit
                      <span className="text-red-500 ml-1">*</span>
                    </label>
                    <input
                      type="number"
                      id="nonCommunityReferrerCredit"
                      value={formData.nonCommunityReferrerCredit === 0 ? "" : formData.nonCommunityReferrerCredit}
                      onChange={handleFieldChange}
                      min="0"
                      placeholder="0"
                      className="w-full p-3 rounded-lg bg-lightblue-100 text-xs text-grease-100 focus:outline-none border-none"
                      disabled={loading}
                    />
                  </div>
                  <div className="flex-1">
                    <label
                      className="block text-xs text-gray-500 mb-2"
                      htmlFor="nonCommunityRefereeCredit"
                    >
                      Non-Community Referee Credit
                      <span className="text-red-500 ml-1">*</span>
                    </label>
                    <input
                      type="number"
                      id="nonCommunityRefereeCredit"
                      value={formData.nonCommunityRefereeCredit === 0 ? "" : formData.nonCommunityRefereeCredit}
                      onChange={handleFieldChange}
                      min="0"
                      placeholder="0"
                      className="w-full p-3 rounded-lg bg-lightblue-100 text-xs text-grease-100 focus:outline-none border-none"
                      disabled={loading}
                    />
                  </div>
                </div>

                {/* Community Count */}
                <div className="mb-4">
                  <label
                    className="block text-xs text-gray-500 mb-2"
                    htmlFor="communityCount"
                  >
                    Community Count
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                  <input
                    type="number"
                    id="communityCount"
                    value={formData.communityCount === 0 ? "" : formData.communityCount}
                    onChange={handleFieldChange}
                    min="0"
                    placeholder="0"
                    className="w-full p-3 rounded-lg bg-lightblue-100 text-xs text-grease-100 focus:outline-none border-none"
                    disabled={loading}
                  />
                </div>

                {/* Valid Till */}
                <div className="mb-4">
                  <label
                    className="block text-xs text-gray-500 mb-2"
                    htmlFor="validTill"
                  >
                    Valid Till
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                  <DatePicker
                    id="validTill"
                    selected={
                      formData.validTill ? new Date(formData.validTill) : null
                    }
                    onChange={handleValidTillChange}
                    dateFormat="dd/MM/yyyy"
                    className="w-full p-3 rounded-lg bg-lightblue-100 text-xs text-grease-100 focus:outline-none disabled:bg-gray-200 border-none"
                    placeholderText="Select date (DD/MM/YYYY)"
                    minDate={new Date()}
                    disabled={loading}
                    withPortal={isMobile}
                    onFocus={(e) =>
                      e.target.setAttribute("autocomplete", "off")
                    }
                  />
                </div>

                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 text-xs text-gray-600 disabled:opacity-50"
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-[#FFD700] text-white text-xs rounded-lg hover:bg-[#E6C200] transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
                    disabled={loading}
                  >
                    {loading ? "Saving..." : "Save"}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  },
);

EditReferralModal.displayName = "EditReferralModal";

export default EditReferralModal;
